import { defaultLocale, isLocale, locales, type Locale } from "./i18n";
import { isGuideId, type GuideId } from "./guides";
import { isYouTubeGuideId, isYouTubeGuideLocale, type YouTubeGuideId } from "./youtubeGuide";

export type LegalPageKind = "privacy" | "terms" | "copyright" | "contact";
export type IntentPageKind = "youtube-to-mp4" | "youtube-to-mp3" | "youtube-shorts-downloader" | "youtube-4k-downloader" | "instagram-reel-downloader" | "instagram-photo-downloader" | "instagram-story-downloader" | "facebook-reels-downloader" | "twitter-gif-downloader";
export type YouTubeIntentPageKind = Extract<IntentPageKind, `youtube-${string}`>;
export type InstagramIntentPageKind = Extract<IntentPageKind, `instagram-${string}`>;
export type FacebookIntentPageKind = Extract<IntentPageKind, `facebook-${string}`>;
export type PageKind = "home" | "desktop" | "downloader" | "threads-downloader-compatibility" | "instagram-downloader" | "youtube-downloader" | "facebook-downloader" | "tiktok-downloader" | "douyin-downloader" | "x-downloader" | "bilibili-downloader" | IntentPageKind | "guides" | GuideId | YouTubeGuideId | LegalPageKind | "not-found";

export const intentPageKinds: readonly IntentPageKind[] = [
  "youtube-to-mp4",
  "youtube-to-mp3",
  "youtube-shorts-downloader",
  "youtube-4k-downloader",
  "instagram-reel-downloader",
  "instagram-photo-downloader",
  "instagram-story-downloader",
  "facebook-reels-downloader",
  "twitter-gif-downloader"
];

export const indexableIntentPageKinds: readonly IntentPageKind[] = intentPageKinds.filter((page) => page !== "instagram-story-downloader");

export function isIntentPageKind(page: PageKind): page is IntentPageKind {
  return intentPageKinds.includes(page as IntentPageKind);
}

export function isYouTubeIntentPageKind(page: PageKind): page is YouTubeIntentPageKind {
  return isIntentPageKind(page) && page.startsWith("youtube-");
}

export function isInstagramIntentPageKind(page: PageKind): page is InstagramIntentPageKind {
  return isIntentPageKind(page) && page.startsWith("instagram-");
}

export function isFacebookIntentPageKind(page: PageKind): page is FacebookIntentPageKind {
  return isIntentPageKind(page) && page.startsWith("facebook-");
}

export function isIndexableIntentPageKind(page: PageKind): page is IntentPageKind {
  return isIntentPageKind(page) && indexableIntentPageKinds.includes(page);
}

export function intentPlatform(page: IntentPageKind): "youtube" | "instagram" | "facebook" | "x" {
  if (page.startsWith("youtube")) return "youtube";
  if (page.startsWith("instagram")) return "instagram";
  if (page.startsWith("facebook")) return "facebook";
  return "x";
}

const completeLocaleOverrides: Partial<Record<PageKind, readonly Locale[]>> = {
  "threads-downloader-compatibility": ["en", "zh-tw", "ja"],
  "youtube-download-formats": ["en", "zh-tw"],
  "download-youtube-videos-android": ["en"],
  "download-youtube-videos-iphone": ["en"],
  "download-youtube-subtitles": ["en", "zh-tw"],
  "youtube-to-mp4": ["en", "zh-tw", "ja", "id"],
  "youtube-to-mp3": ["en", "zh-tw"],
  "youtube-shorts-downloader": ["en", "zh-tw", "ja", "id"],
  "youtube-4k-downloader": ["en", "zh-tw"],
  "instagram-reel-downloader": ["en"],
  "instagram-photo-downloader": ["en"],
  "instagram-story-downloader": ["en"],
  "facebook-reels-downloader": ["en"],
  "twitter-gif-downloader": ["en"]
};

export function completeLocalesForPage(page: PageKind): readonly Locale[] {
  return completeLocaleOverrides[page] || locales;
}

export interface RouteInfo {
  locale: Locale;
  page: PageKind;
}

export function parseRoute(input: string): RouteInfo {
  const pathname = new URL(input, "https://savesilo.invalid").pathname.replace(/\/+$/, "") || "/";
  const parts = pathname.split("/").filter(Boolean);
  const explicitLocale = isLocale(parts[0]) ? parts[0] : undefined;
  const localized = explicitLocale !== undefined;
  const locale = explicitLocale ?? defaultLocale;
  if ((localized && parts.length === 1) || (!localized && parts.length === 0)) return { locale, page: "home" };
  const slug = localized ? parts[1] : parts[0];
  const nestedSlug = localized ? parts[2] : parts[1];
  if (slug === "guides") {
    if (!nestedSlug) return { locale, page: "guides" };
    if (isGuideId(nestedSlug) && parts.length === (localized ? 3 : 2)) return { locale, page: nestedSlug };
    if (isYouTubeGuideId(nestedSlug) && isYouTubeGuideLocale(nestedSlug, locale) && parts.length === (localized ? 3 : 2)) return { locale, page: nestedSlug };
    return { locale, page: "not-found" };
  }
  if (parts.length !== (localized ? 2 : 1)) return { locale, page: "not-found" };
  if (slug === "silo-downloader") return completeLocalesForPage("desktop").includes(locale) ? { locale, page: "desktop" } : { locale, page: "not-found" };
  switch (slug) {
    case "desktop":
      return completeLocalesForPage(slug).includes(locale) ? { locale, page: slug } : { locale, page: "not-found" };
    case "threads-downloader":
      return { locale, page: "downloader" };
    case "threads-downloader-compatibility":
      return completeLocalesForPage(slug).includes(locale) ? { locale, page: slug } : { locale, page: "not-found" };
    case "instagram-downloader":
      return { locale, page: "instagram-downloader" };
    case "youtube-downloader":
      return { locale, page: "youtube-downloader" };
    case "youtube-to-mp4":
    case "youtube-to-mp3":
    case "youtube-shorts-downloader":
    case "youtube-4k-downloader":
    case "instagram-reel-downloader":
    case "instagram-photo-downloader":
    case "instagram-story-downloader":
    case "facebook-reels-downloader":
    case "twitter-gif-downloader":
      return completeLocalesForPage(slug).includes(locale) ? { locale, page: slug } : { locale, page: "not-found" };
    case "facebook-downloader":
      return { locale, page: "facebook-downloader" };
    case "tiktok-downloader":
      return { locale, page: "tiktok-downloader" };
    case "douyin-downloader":
      return { locale, page: "douyin-downloader" };
    case "twitter-video-downloader":
      return { locale, page: "x-downloader" };
    case "bilibili-downloader":
      return { locale, page: "bilibili-downloader" };
    case "privacy":
    case "terms":
    case "copyright":
    case "contact":
      return { locale, page: slug };
    default:
      return { locale, page: "not-found" };
  }
}

export function localizedPath(locale: Locale, page: Exclude<PageKind, "not-found">): string {
  const suffix = page === "home"
    ? ""
    : page === "downloader"
      ? "/threads-downloader"
      : page === "x-downloader"
        ? "/twitter-video-downloader"
        : page === "guides"
          ? "/guides"
          : isGuideId(page) || isYouTubeGuideId(page)
            ? `/guides/${page}`
            : `/${page}`;
  return locale === defaultLocale ? suffix || "/" : `/${locale}${suffix}`;
}

export function canonicalRedirectPath(pathname: string): string {
  if (pathname === "/silo-downloader" || pathname === "/silo-downloader/") return "/desktop";
  if (pathname === "/en/silo-downloader" || pathname === "/en/silo-downloader/") return "/desktop";
  if (pathname === "/zh-tw/silo-downloader" || pathname === "/zh-tw/silo-downloader/") return "/zh-tw/desktop";
  if (pathname === "/en" || pathname === "/en/") return "/";
  if (pathname.startsWith("/en/")) return pathname.slice(3) || "/";
  return "";
}
